import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Input, Spin } from "antd";
import moment from "moment";
import Swal from "sweetalert2";
import { Filter as FilterOutlined, Edit as EditOutlined, Trash2 as DeleteOutlined } from "react-feather";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
const token = localStorage.getItem("token");

const TaxList = () => {
  const [searchText, setSearchText] = useState("");
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const { Search } = Input;
  const maximizeBtnRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [editingTax, setEditingTax] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    rate: "",
    is_active: true,
  });

  useEffect(() => {
    fetchTaxes();
  }, []);

  const fetchTaxes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiURL}/pos/tax/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Taxes API Response:", response.data);
      const fetchedTaxes = Array.isArray(response.data) ? response.data : response.data.taxes || [];
      // Ensure rate is a number
      const normalizedTaxes = fetchedTaxes.map((tax) => ({
        ...tax,
        rate: parseFloat(tax.rate) || 0, // Convert to number, default to 0 if invalid
      }));
      setTaxes(normalizedTaxes);
      if (normalizedTaxes.length === 0) {
        setError("No taxes found");
      } else {
        setError(null);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to fetch taxes";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredTaxes = taxes.filter((tax) => {
    const matchesSearch = tax.name.toLowerCase().includes(searchText.toLowerCase());
    const matchesActive = activeFilter === null || tax.is_active === (activeFilter.value === "active");
    return matchesSearch && matchesActive;
  });

  const activeOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const handleFilterReset = () => {
    setActiveFilter(null);
    setSearchText("");
  };

  const getDynamicHeight = () => {
    return window.innerHeight - 150;
  };

  const showModal = (mode, tax = null) => {
    setModalMode(mode);
    if (mode === "edit" && tax) {
      setEditingTax(tax);
      setFormData({
        name: tax.name,
        rate: tax.rate.toString(),
        is_active: tax.is_active,
      });
    } else {
      setEditingTax(null);
      setFormData({
        name: "",
        rate: "",
        is_active: true,
      });
    }
    setIsModalVisible(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const { name, rate, is_active } = formData;

    if (!name || !rate) {
      toast.error("Name and rate are required");
      return;
    }

    const payload = {
      name,
      rate: parseFloat(rate),
      is_active,
    };

    try {
      let updatedTax;
      if (modalMode === "add") {
        const response = await axios.post(`${apiURL}/pos/tax/add`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        updatedTax = response.data;
        setTaxes([...taxes, updatedTax]);
        toast.success("Tax added successfully!");
      } else {
        const response = await axios.put(`${apiURL}/pos/tax/edit/${editingTax.tax_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        updatedTax = response.data;
        setTaxes(taxes.map((t) => (t.tax_id === editingTax.tax_id ? updatedTax : t)));
        toast.success("Tax updated successfully!");
      }
      setIsModalVisible(false);
      setFormData({
        name: "",
        rate: "",
        is_active: true,
      });
      setEditingTax(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to save tax";
      toast.error(errorMessage);
    }
  };

  const handleDelete = (tax_id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the tax and may affect associated menu items!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${apiURL}/pos/tax/delete/${tax_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setTaxes(taxes.filter((t) => t.tax_id !== tax_id));
          toast.success("Tax deleted successfully!");
        } catch (err) {
          const errorMessage = err.response?.data?.error || "Failed to delete tax";
          toast.error(errorMessage);
        }
      }
    });
  };

  const handleToggleStatus = async (tax_id, currentStatus) => {
    try {
      const response = await axios.put(
        `${apiURL}/pos/tax/edit/${tax_id}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedTax = response.data;
      setTaxes(taxes.map((t) => (t.tax_id === tax_id ? updatedTax : t)));
      toast.success("Tax status updated successfully!");
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to update tax status";
      toast.error(errorMessage);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (name) => <span className="colored-box tax-name-box">{name}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 200,
      fixed: "left",
    },
    {
      title: "Rate (%)",
      dataIndex: "rate",
      render: (rate) => {
        const rateNumber = typeof rate === "number" ? rate : parseFloat(rate) || 0;
        return <span className="colored-box rate-box">{rateNumber.toFixed(2)}%</span>;
      },
      sorter: (a, b) => (parseFloat(a.rate) || 0) - (parseFloat(b.rate) || 0),
      width: 150,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      render: (is_active, record) => (
        <div className="toggle-switch">
          <input
            type="checkbox"
            id={`toggle-${record.tax_id}`}
            checked={is_active}
            onChange={() => handleToggleStatus(record.tax_id, is_active)}
            hidden
          />
          <label htmlFor={`toggle-${record.tax_id}`} className="toggle-label">
            <span className="toggle-inner" />
            <span className="toggle-circle" />
          </label>
        </div>
      ),
      width: 150,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date) => moment(date).format("DD MMM YYYY HH:mm"),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      width: 200,
    },
    {
      title: "Actions",
      dataIndex: "tax_id",
      render: (tax_id, record) => (
        <div>
          <Button type="link" icon={<EditOutlined />} onClick={() => showModal("edit", record)} />
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(tax_id)} />
        </div>
      ),
      width: 120,
      fixed: "right",
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title">Taxes</h3>
            </div>
            <div className="col-md-10 d-flex justify-content-between align-items-center">
              <Search
                placeholder="Search by Name"
                allowClear
                enterButton="Search"
                size="large"
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 400 }}
              />
              <div className="d-flex align-items-center">
                <div className="view-icons me-2">
                  <Button type="link" data-bs-toggle="modal" data-bs-target="#filterModal">
                    <FilterOutlined />
                  </Button>
                  <Button type="link" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Button>
                </div>
                <Button className="btn add-btn custom-btn" onClick={() => showModal("add")}>
                  <i className="la la-plus-circle custom-icon" /> Add Tax
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive" style={{ minHeight: "60vh", height: "calc(100vh - 150px)", overflow: "auto" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <p className="text-danger text-center" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {error}
            </p>
          ) : filteredTaxes.length === 0 ? (
            <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <img src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg" alt="No data available" style={{ width: "150px", marginBottom: "20px" }} />
              <h3>No Data Available</h3>
              <p>No taxes found.</p>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredTaxes}
              pagination={{ pageSize: 20 }}
              className="table-striped custom-table"
              rowKey={(record) => record.tax_id}
              scroll={{ x: 700, y: getDynamicHeight() }}
            />
          )}
        </div>

        {/* Filter Modal */}
        <div className="modal custom-modal fade" id="filterModal" tabIndex={-1} role="dialog" aria-labelledby="filterModalTitle" aria-hidden="true">
          <div className="modal-dialog modal-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%", zIndex: 1060 }} role="document">
            <div className="modal-content h-100">
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalTitle">Filter</h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Active:</label>
                      <Select
                        options={activeOptions}
                        placeholder="Select Status"
                        value={activeFilter}
                        onChange={setActiveFilter}
                        isClearable
                        styles={{ control: (base) => ({ ...base, minWidth: "300px", borderRadius: 4, borderColor: '#d9d9d9' }), menu: (base) => ({ ...base, zIndex: 1070 }), menuPortal: (base) => ({ ...base, zIndex: 1070 }) }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                <button type="button" className="btn btn-light" onClick={handleFilterReset} data-bs-dismiss="modal">Reset</button>
                <button type="button" className="btn btn-primary" data-bs-dismiss="modal">Apply</button>
              </div>
            </div>
          </div>
        </div>

        {/* Unified Add/Edit Tax Modal */}
        <div className={`modal custom-modal fade ${isModalVisible ? 'show' : ''}`} id="taxModal" tabIndex={-1} role="dialog" aria-labelledby="taxModalTitle" aria-hidden={!isModalVisible} style={{ display: isModalVisible ? 'block' : 'none' }}>
          <div className="modal-dialog modal-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%", zIndex: 1060 }} role="document">
            <div className="modal-content h-100" style={{ borderRadius: "10px", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
              <div className="modal-header" style={{ background: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}>
                <h5 className="modal-title" id="taxModalTitle">{modalMode === "add" ? "Add Tax" : "Edit Tax"}</h5>
                <button type="button" className="btn-close" onClick={() => setIsModalVisible(false)} aria-label="Close"></button>
              </div>
              <form onSubmit={handleModalSubmit}>
                <div className="modal-body" style={{ maxHeight: "calc(100vh - 140px)", overflowY: "auto", padding: "20px" }}>
                  <div className="form-group mb-3">
                    <label className="form-label">Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      placeholder="Enter tax name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Rate (%) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      name="rate"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter tax rate"
                      value={formData.rate}
                      onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Active</label>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="modal-is-active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        hidden
                      />
                      <label htmlFor="modal-is-active" className="toggle-label">
                        <span className="toggle-inner" />
                        <span className="toggle-circle" />
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ position: "absolute", bottom: 0, width: "100%", background: "#f8f9fa", borderTop: "1px solid #e9ecef", padding: "10px" }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setIsModalVisible(false)}>Close</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxList;